import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StorefrontLayout from '../components/layout/StorefrontLayout';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { applyContentLocaleToUrl } from '../utils/contentLocaleQuery';
import { departmentPath, useDepartmentSlug } from '../utils/departmentRouting';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type Detail = {
  id: string;
  name: string;
  slug: string;
  headline?: string | null;
  blurb?: string | null;
  heroImageUrl?: string | null;
  priceUsd: number | string;
  items: Array<{
    qty: number;
    unit: string;
    product: {
      id: string;
      name: string;
      imageUrl?: string | null;
      baseUnit: string;
      pricePerUnit: unknown;
      salePrice?: unknown;
      isActive: boolean;
    };
  }>;
};

export default function PackageDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const departmentSlug = useDepartmentSlug();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { addLine } = useCart();
  const { formatPrice } = useCurrency();

  const [pkg, setPkg] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const url = new URL(
          `${API_BASE}/catalog/packages/slug/${encodeURIComponent(slug)}`,
        );
        if (departmentSlug) {
          url.searchParams.set('departmentSlug', departmentSlug);
        }
        const res = await fetch(
          applyContentLocaleToUrl(
            url.toString(),
            i18n.resolvedLanguage || i18n.language,
          ),
        );
        if (!res.ok) throw new Error('404');
        const data = (await res.json()) as Detail;
        if (!cancelled) setPkg(data);
      } catch {
        if (!cancelled) {
          setError(t('storefront.package_not_found', { defaultValue: 'This package is not available.' }));
          setPkg(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [departmentSlug, slug, t, i18n.language, i18n.resolvedLanguage]);

  const hero =
    pkg?.heroImageUrl && pkg.heroImageUrl.startsWith('http')
      ? pkg.heroImageUrl
      : pkg?.heroImageUrl
        ? `${API_BASE}${pkg.heroImageUrl}`
        : null;

  const price = pkg ? Number(pkg.priceUsd) : 0;
  const oldTotal = pkg
    ? pkg.items.reduce((sum, row) => {
        const unitPrice = Number(row.product.salePrice ?? row.product.pricePerUnit ?? 0);
        const qty = Number(row.qty ?? 0);
        if (!Number.isFinite(unitPrice) || unitPrice <= 0) return sum;
        if (!Number.isFinite(qty) || qty <= 0) return sum;
        return sum + unitPrice * qty;
      }, 0)
    : 0;

  return (
    <StorefrontLayout mainClassName="bg-slate-50" contentContained>
      <div className="mx-auto max-w-4xl px-4 py-6 sm:py-8">
        <Link
          to={departmentPath(departmentSlug, '/packages')}
          className="text-sm font-medium text-slate-600 hover:text-slate-900"
        >
          ← {t('storefront.packages_back', { defaultValue: 'All packages' })}
        </Link>

        {loading && <p className="mt-8 text-sm text-slate-600">{t('common.loading')}</p>}
        {error && (
          <p className="mt-8 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        {pkg && !loading && (
          <div className="mt-6 space-y-8">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="aspect-[21/9] w-full bg-slate-900">
                {hero ? (
                  <img src={hero} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-slate-500">
                    {t('storefront.package_no_image', { defaultValue: 'Bundle' })}
                  </div>
                )}
              </div>
              <div className="p-6 sm:p-8">
                <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">{pkg.name}</h1>
                {pkg.headline ? <p className="mt-2 text-lg text-slate-700">{pkg.headline}</p> : null}
                {pkg.blurb ? <p className="mt-3 text-sm text-slate-600">{pkg.blurb}</p> : null}
                <div className="mt-4">
                  {oldTotal > 0 ? (
                    <p className="text-lg font-extrabold text-rose-500 line-through decoration-2">
                      {formatPrice(oldTotal)}
                    </p>
                  ) : null}
                  <p className="text-3xl font-extrabold text-emerald-600">{formatPrice(price)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    addLine({ kind: 'package', packageId: pkg.id, qty: 1 });
                    navigate('/cart');
                  }}
                  className="mt-6 w-full rounded-full bg-[#00b853] px-8 py-3 text-sm font-semibold text-white hover:bg-[#009946] sm:w-auto"
                >
                  {t('storefront.add_to_cart', { defaultValue: 'Add to cart' })}
                </button>
              </div>
            </div>

            <section>
              <h2 className="text-sm font-semibold text-slate-900">
                {t('storefront.package_includes', { defaultValue: 'Included in this bundle' })}
              </h2>
              <ul className="mt-3 divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
                {pkg.items.map((row) => (
                  <li key={`${row.product.id}-${row.unit}`} className="flex flex-col items-start gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
                    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-slate-100">
                      {row.product.imageUrl ? (
                        <img
                          src={
                            row.product.imageUrl.startsWith('http')
                              ? row.product.imageUrl
                              : `${API_BASE}${row.product.imageUrl}`
                          }
                          alt=""
                          className="h-full w-full object-contain"
                        />
                      ) : null}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="break-words font-medium text-slate-900">{row.product.name}</p>
                      <p className="text-xs text-slate-500">
                        {row.qty} {row.unit}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </div>
    </StorefrontLayout>
  );
}
